import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '../../../components/ui/button';
import { useAuth } from '../../../contexts/AuthContext';
import { useAuthActions } from '../../auth/lib/actions';
import redirectTo from '../../../utils/redirect';
import { useLocation } from 'react-router-dom';
import logoImage from '../../../assets/logo.png';
interface HeaderProps {
  onSignInClick: () => void;
  onImportFromLumaClick?: () => void;
  importButtonStyle?: 'gradient' | 'black';
  theme?: 'light' | 'dark';
}

export const Header: React.FC<HeaderProps> = React.memo(({ onSignInClick, onImportFromLumaClick, importButtonStyle = 'gradient', theme = 'light' }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isAuthenticated, user } = useAuth();
  const {signOut} = useAuthActions();

  const isDark = theme === 'dark';
  const location = useLocation();

  const shouldDesaturateLogo = useMemo(() => {
    const p = location.pathname || '/';
    if (p === '/' || p === '/create') return false;
    if (/^\/events\/[^/]+\/edit$/.test(p)) return false;
    if (/^\/event\/[^/]+$/.test(p)) return false;

    return true;
  }, [location.pathname]);

  const handleSignInClick = useCallback(() => {
    setIsMenuOpen(false);
    onSignInClick();
  }, [onSignInClick]);

  const handleMenuToggle = useCallback(() => {
    setIsMenuOpen(prev => !prev);
  }, []);

  const handleMenuClose = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  const handleSignOut = useCallback(async () => {
    try {
      await signOut();
      // Optionally, you can add any client-side cleanup here if needed,
      // though signOut from AuthActions should handle Supabase session.
    } catch (error) {
      console.error("Error signing out: ", error);
      // Handle error appropriately, e.g., show a notification to the user
    }
    setIsMenuOpen(false);
  }, [signOut]);

  // Cerrar el menú cuando el usuario se autentica
  useEffect(() => {
    if (isAuthenticated) {
      setIsMenuOpen(false);
    }
  }, [isAuthenticated]);


  return (
    <>
      <header className="absolute top-0 left-0 right-0 z-10 p-0 px-6">
        <div className="mx-auto flex justify-between items-center">
          <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-800'} cursor-pointer`} onClick={() => redirectTo('/')}>
            <img src={logoImage} alt="Punta Tech Meetup" style={{width: '120px'}}/>
          </div>
          
          <div className="flex items-center gap-4 relative">
            {onImportFromLumaClick && (
              // Import from Luma button; gradient by default, black text on create page
              <Button 
                variant="ghost"
                onClick={onImportFromLumaClick}
                className={
                  importButtonStyle === 'black'
                    ? "bg-transparent hover:bg-transparent px-0 text-white"
                    : "bg-transparent hover:bg-transparent px-0 text-transparent bg-clip-text bg-gradient-to-r from-primary via-primary to-primary"
                }
              >
                Importar desde Luma
              </Button>
            )}
            <div className="relative">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={handleMenuToggle}
                className={`${isMenuOpen ? (isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-900') : (isDark ? 'text-white' : '')} ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </Button>
              
              {/* Dropdown Menu */}
              {isMenuOpen && (
                <div className={`absolute right-0 mt-2 rounded-lg shadow-lg border py-2 z-20 center ${isDark ? 'bg-black border-white/15' : 'bg-white border-gray-200'}`} style={{minWidth: '200px'}}>
                  {isAuthenticated ? (
                    <>
                      <div className={`px-4 py-2 text-sm ${isDark ? 'text-gray-300 border-white/10' : 'text-gray-500 border-gray-200'} border-b`}>
                        {user?.email}
                      </div>
                      <button onClick={() => { redirectTo('/dashboard') }} className={`w-full text-left px-4 py-3 transition-colors ${isDark ? 'text-white hover:bg-white/10' : 'text-gray-700 hover:bg-gray-50'}`}>
                        Panel
                      </button>
                      <button 
                        onClick={handleSignOut}
                        className={`w-full text-left px-4 py-3 transition-colors ${isDark ? 'text-white hover:bg-white/10' : 'text-gray-700 hover:bg-gray-50'}`}
                      >
                        Cerrar sesión
                      </button>
                    </>
                  ) : (
                    <>
                      <button className={`w-full text-left px-4 py-3 transition-colors ${isDark ? 'text-white hover:bg-white/10' : 'text-gray-700 hover:bg-gray-50'}`}>
                        Sobre nosotros
                      </button>
                      <button className={`w-full text-left px-4 py-3 transition-colors ${isDark ? 'text-white hover:bg-white/10' : 'text-gray-700 hover:bg-gray-50'}`}>
                        Contacto
                      </button>
                      <button 
                        onClick={handleSignInClick}
                        className={`w-full text-left px-4 py-3 transition-colors ${isDark ? 'text-white hover:bg-white/10' : 'text-gray-700 hover:bg-gray-50'}`}
                      >
                        Iniciar sesión
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Overlay to close menu */}
        {isMenuOpen && (
          <div 
            className="fixed inset-0 z-10" 
            onClick={handleMenuClose}
          />
        )}
      </header>
    </>
  );
});

Header.displayName = 'Header';